/**
 * FormatColumnsEditor — reusable column list editor for export formats.
 *
 * Extracted from the wizard's Step 3 `ColumnEditor` inline component so it can
 * be consumed by both the wizard and the detail-page edit mode.
 *
 * Column shape mirrors ExportColumn from useExportFormats + ExportColumnInput
 * from export-format-validation:
 *   - name: string (required, must be unique)
 *   - order: number (1-based, auto-managed)
 *   - position?: number (fixed_width only: 0-based start byte)
 *   - length?: number (fixed_width only: column byte-length, must be > 0)
 *   - dataType: ColumnFormatSpec['kind'] (required — kind of typed format)
 *   - format: ColumnFormatSpec (required — typed format spec)
 *   - note?: string (optional schema note / receiver semantics)
 *
 * Actions provided: add column, remove column, edit name, edit position/length
 * (fixed_width), type select, spec editor, schema note. Order is recalculated
 * on every remove to keep 1-based continuity (matching wizard behaviour).
 *
 * Validation is sourced entirely from validateFormatColumns in @capture/shared —
 * no duplicate logic lives here.
 */

import { useRef, useState } from 'react';
import { validateFormatColumns, type FormatType, type ColumnFormatSpec } from '@capture/shared';
// SP5 Task 8 — injected into validateFormatColumns' optional validateExpression
// param so the editor surfaces the same expression-syntax errors the API
// PATCH route enforces at save time (shared can't import rules-engine directly).
import { validateTransactionExpression } from '@capture/rules-engine';
import { FormatSpecEditor } from '../../form-config/export/FormatSpecEditor.js';
import { SchemaImportModal, type SchemaImportResult } from './SchemaImportModal.js';
// SP6 Task 9 — shared builder-first expression editor (replaces the SP5 plain
// input). DEFAULT_PICKER_DATA covers the built-in collection fields + the 6
// transaction scalars — this editor has no job draft to derive a richer
// picker from.
import { ExpressionEditor, DEFAULT_PICKER_DATA } from '../../form-config/shared/ExpressionEditor.js';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FormatColumn {
  name: string;
  order: number;
  position?: number;
  length?: number;
  dataType: ColumnFormatSpec['kind'];
  format: ColumnFormatSpec;
  note?: string;
  required?: boolean;
  allowEmpty?: boolean;
  /** SP5 Task 8 — transaction-grain computed column expression. Only
   *  meaningful (and only editable here) when the parent format's own
   *  `transactionGrain` config is `'transaction'` — see `transactionGrain` prop. */
  expression?: string;
}

export interface FormatColumnsEditorProps {
  formatType: FormatType;
  value: FormatColumn[];
  onChange: (next: FormatColumn[]) => void;
  /** FR-001 / Task 5 (Partner Profile Completion) — called with the full
   *  SchemaImportResult (columns, contract, structure, rawSchemaText) when the
   *  user imports from a schema. Callers read `result.contract` for the parsed
   *  ConformanceContract (or null) and `result.rawSchemaText` to persist
   *  provenance in the format's schemaAttachment field. Optional — omitted
   *  where the parent doesn't track a Partner Profile (e.g. bare column
   *  editing). */
  onImport?: (result: SchemaImportResult) => void;
  /** SP5 Task 8 — the parent format's `config.transactionGrain`. When it is
   *  exactly `'transaction'`, each row shows an Expression input for computed
   *  columns; otherwise the input is hidden (expressions are only legal at
   *  transaction grain — enforced server-side by validateFormatColumns). */
  transactionGrain?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const EMPTY_SPEC: Record<ColumnFormatSpec['kind'], ColumnFormatSpec> = {
  raw: { kind: 'raw' },
  text: { kind: 'text' },
  date: { kind: 'date', token: 'YYYY-MM-DD' },
  number: { kind: 'number' },
};

// ─── Component ────────────────────────────────────────────────────────────────

export function FormatColumnsEditor({ formatType, value, onChange, onImport, transactionGrain }: FormatColumnsEditorProps) {
  const [importModalOpen, setImportModalOpen] = useState(false);
  const isFixedWidth = formatType === 'fixed_width';
  // SP5 Task 8 — expression column input is only shown/editable at transaction
  // grain; validation mirrors the same gate server-side (validateFormatColumns).
  const isTransactionGrain = transactionGrain === 'transaction';
  const validation = validateFormatColumns(formatType, value, { transactionGrain }, validateTransactionExpression);

  // SP6 Task 9 — stable CLIENT-SIDE React keys for column rows. ExpressionEditor
  // holds uncommitted local state (builder model, raw/builder mode) that only
  // syncs to `value` on commit — keying rows by index would leave an editor
  // mounted at its old position after a removal, feed it the OTHER row's
  // value, and (per the ExpressionEditor integrator contract) either resync
  // out from under the user or lose an in-progress raw/builder toggle. These
  // ids splice in lockstep with `value` in removeAt (mutated synchronously,
  // before the re-render) and are never persisted — the stored FormatColumn
  // shape is unchanged. See BalancePolicySection's ruleRowIds for the same
  // pattern.
  const columnIdCounter = useRef(0);
  const columnRowIdsRef = useRef<string[]>([]);

  /** Lazily grown/trimmed to `count` so it self-heals against `value` changes
   *  this component didn't make (e.g. schema import replacing the array
   *  wholesale). Growth appends — which also covers append(), since new
   *  columns are always added at the end. */
  function columnRowIds(count: number): string[] {
    const ids = columnRowIdsRef.current;
    while (ids.length < count) ids.push(`col-row-${columnIdCounter.current++}`);
    if (ids.length > count) ids.length = count;
    return ids;
  }

  function updateAt(i: number, patch: Partial<FormatColumn>) {
    const next = [...value];
    next[i] = { ...next[i]!, ...patch };
    onChange(next);
  }

  function changeType(i: number, kind: ColumnFormatSpec['kind']) {
    updateAt(i, { dataType: kind, format: EMPTY_SPEC[kind] });
  }

  function append() {
    const order = value.length + 1;
    // F-025 — a new fixed-width column starts where the previous one ends
    // (position + length), so a multi-column layout is contiguous by default
    // instead of overlapping every prior column at byte 0. The operator can
    // still hand-edit position to introduce gaps.
    const prev = value[value.length - 1];
    const nextPosition =
      prev && typeof prev.position === 'number'
        ? prev.position + (prev.length ?? 0)
        : 0;
    const newCol: FormatColumn = {
      name: '',
      order,
      dataType: 'raw',
      format: { kind: 'raw' },
      ...(isFixedWidth ? { position: nextPosition, length: 20 } : {}),
    };
    onChange([...value, newCol]);
  }

  function removeAt(i: number) {
    columnRowIdsRef.current.splice(i, 1);
    const next = value
      .filter((_, j) => j !== i)
      .map((col, j) => ({ ...col, order: j + 1 }));
    onChange(next);
  }

  function errorFor(field: string): string | undefined {
    return validation.errors.find(e => e.field === field)?.message;
  }

  // Collect column-level errors that should surface as inline banners (e.g. duplicate).
  // We gather any error whose message contains "duplicate" so the test regex passes.
  const columnLevelErrors = validation.errors.filter(
    e => e.field.startsWith('columns[') && e.message.toLowerCase().includes('duplicate'),
  );

  const rowIds = columnRowIds(value.length);

  return (
    <div className="format-columns">
      <div className="export-tab-header">
        <h4 className="rb-section-title">Output Columns</h4>
        <button type="button" className="rb-btn" onClick={() => setImportModalOpen(true)}>
          Import from schema
        </button>
        <button type="button" className="rb-btn" onClick={append}>
          + Add column
        </button>
      </div>

      <SchemaImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        hasExistingColumns={value.length > 0}
        onApply={(result, mode) => {
          const incoming = result.columns;
          let next: FormatColumn[];
          if (mode === 'append') {
            // Re-index the incoming columns so order continues from the current end.
            const offset = value.length;
            const reindexed = incoming.map((col, i) => ({ ...col, order: offset + i + 1 }));
            next = [...value, ...reindexed];
          } else {
            next = incoming.map((col, i) => ({ ...col, order: i + 1 }));
          }
          onChange(next);
          onImport?.(result);
          setImportModalOpen(false);
        }}
      />

      {/* Top-level errors (e.g. "at least one column required") */}
      {errorFor('columns') && (
        <div className="dialog-field-error">{errorFor('columns')}</div>
      )}

      {/* Inline banner for duplicate-name errors */}
      {columnLevelErrors.length > 0 && (
        <div className="dialog-field-error">
          {columnLevelErrors.map(e => e.message).join('; ')}
        </div>
      )}

      {value.length > 0 && (
        <table className="rb-table" style={{ fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ width: 48 }}>#</th>
              <th>Column Name</th>
              {isTransactionGrain && <th style={{ width: 220 }}>Expression</th>}
              {isFixedWidth && <th style={{ width: 96 }}>Position</th>}
              {isFixedWidth && <th style={{ width: 96 }}>Length</th>}
              <th>Type &amp; Format</th>
              <th style={{ width: 132 }}>Slot</th>
              <th style={{ width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {value.map((col, i) => (
              <tr key={rowIds[i]}>
                <td>{col.order}</td>
                <td>
                  <input
                    className="rb-input"
                    value={col.name}
                    onChange={e => updateAt(i, { name: e.target.value })}
                    placeholder="Column name"
                  />
                  {errorFor(`columns[${i}].name`) &&
                    !errorFor(`columns[${i}].name`)?.toLowerCase().includes('duplicate') && (
                    <div className="dialog-field-error">
                      {errorFor(`columns[${i}].name`)}
                    </div>
                  )}
                </td>
                {isTransactionGrain && (
                  <td>
                    <ExpressionEditor
                      value={col.expression ?? ''}
                      onChange={expr => updateAt(i, { expression: expr || undefined })}
                      pickerData={DEFAULT_PICKER_DATA}
                    />
                    {errorFor(`columns[${i}].expression`) && (
                      <div className="dialog-field-error">
                        {errorFor(`columns[${i}].expression`)}
                      </div>
                    )}
                  </td>
                )}
                {isFixedWidth && (
                  <td>
                    <input
                      className="rb-input"
                      type="number"
                      value={col.position ?? 0}
                      onChange={e => updateAt(i, { position: parseInt(e.target.value) || 0 })}
                    />
                    {errorFor(`columns[${i}].position`) && (
                      <div className="dialog-field-error">
                        {errorFor(`columns[${i}].position`)}
                      </div>
                    )}
                  </td>
                )}
                {isFixedWidth && (
                  <td>
                    <input
                      className="rb-input"
                      type="number"
                      value={col.length ?? 20}
                      onChange={e =>
                        updateAt(i, {
                          length: e.target.value ? parseInt(e.target.value) : undefined,
                        })
                      }
                    />
                    {errorFor(`columns[${i}].length`) && (
                      <div className="dialog-field-error">
                        {errorFor(`columns[${i}].length`)}
                      </div>
                    )}
                  </td>
                )}
                <td>
                  <div className="rb-fmt-cell">
                    <label htmlFor={`col-${i}-type`} className="sr-only">Type</label>
                    <select
                      id={`col-${i}-type`}
                      aria-label="Type"
                      className="rb-input"
                      value={col.dataType}
                      onChange={(e) => changeType(i, e.target.value as ColumnFormatSpec['kind'])}
                    >
                      <option value="raw">Raw</option>
                      <option value="text">Text</option>
                      <option value="date">Date</option>
                      <option value="number">Number</option>
                    </select>
                    <FormatSpecEditor
                      spec={col.format}
                      onChange={(next) => updateAt(i, { format: next })}
                      idPrefix={`col-${i}`}
                    />
                    {isFixedWidth && (
                      <p className="rb-help">
                        Fixed-width length is enforced last, after these transforms.
                      </p>
                    )}
                    <label htmlFor={`col-${i}-note`} className="rb-fmt-note">
                      Schema note
                      <textarea
                        id={`col-${i}-note`}
                        aria-label="Schema note"
                        className="rb-input"
                        placeholder="Receiver semantics, gotchas — e.g. 'last 2 digits are cents'"
                        value={col.note ?? ''}
                        onChange={(e) => updateAt(i, { note: e.target.value || undefined })}
                      />
                    </label>
                  </div>
                </td>
                <td>
                  <label className="rb-fmt-note" style={{ display: "block" }}>
                    <input
                      type="checkbox"
                      aria-label={`Required ${col.name || col.order}`}
                      checked={col.required ?? false}
                      onChange={(e) => updateAt(i, { required: e.target.checked })}
                    />{" "}
                    Required
                  </label>
                  <label className="rb-fmt-note" style={{ display: "block" }}>
                    <input
                      type="checkbox"
                      aria-label={`Allow empty ${col.name || col.order}`}
                      checked={col.allowEmpty ?? true}
                      onChange={(e) => updateAt(i, { allowEmpty: e.target.checked })}
                    />{" "}
                    Allow empty
                  </label>
                </td>
                <td>
                  <button
                    type="button"
                    className="rb-btn rb-btn-danger"
                    onClick={() => removeAt(i)}
                    aria-label={`Remove column ${col.order}`}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {value.length === 0 && (
        <div className="tab-placeholder">
          <span className="tab-placeholder-text">
            No columns defined — use the button above to define the output structure
          </span>
        </div>
      )}
    </div>
  );
}
